<script setup lang="ts">
import { CheckCircle2, Plus, Pencil, Unlink } from 'lucide-vue-next'

interface LinkedRestaurant {
  storeId: number
  storeName: string
  externalStoreId: string
}

defineProps<{
  title: string
  description: string
  logoSrc: string
  logoAlt: string
  linked: LinkedRestaurant[]
  canAddMore: boolean
  addLabel: string
}>()

const emit = defineEmits<{
  add: []
  edit: [storeId: number]
  unlink: [storeId: number]
}>()

const { t } = useI18n()
</script>

<template>
  <Card>
    <CardHeader class="flex flex-row items-start justify-between gap-3 space-y-0">
      <div class="flex items-center gap-3">
        <img
          :src="logoSrc"
          :alt="logoAlt"
          class="size-10 rounded-lg border border-border bg-background object-contain p-1"
        />
        <div>
          <CardTitle class="text-base">{{ title }}</CardTitle>
          <CardDescription class="text-xs">{{ description }}</CardDescription>
        </div>
      </div>
      <BaseChip
        v-if="linked.length > 0"
        color="green"
        :text="t('pages.reviewPlatforms.configuration.shared.linkedCount', { count: linked.length })"
      >
        <template #leading>
          <CheckCircle2 class="size-3.5" />
        </template>
      </BaseChip>
    </CardHeader>

    <CardContent class="space-y-3">
      <ul v-if="linked.length > 0" class="divide-y divide-border rounded-lg border border-border">
        <li v-for="row in linked" :key="row.storeId" class="flex flex-wrap items-center gap-3 px-3 py-3">
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-foreground">{{ row.storeName }}</p>
            <p class="font-mono text-xs text-muted-foreground truncate">
              {{ row.externalStoreId }}
            </p>
          </div>

          <div class="flex items-center gap-2">
            <Button variant="ghost" size="sm" @click="emit('edit', row.storeId)">
              <Pencil />
              {{ t('pages.reviewPlatforms.configuration.shared.edit') }}
            </Button>
            <Button variant="outline-destructive" size="sm" @click="emit('unlink', row.storeId)">
              <Unlink />
              {{ t('pages.reviewPlatforms.configuration.shared.unlink') }}
            </Button>
          </div>
        </li>
      </ul>

      <p
        v-else
        class="rounded-lg border border-dashed border-border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground"
      >
        {{ t('pages.reviewPlatforms.configuration.shared.empty') }}
      </p>

      <Button v-if="canAddMore" variant="outline" size="sm" @click="emit('add')">
        <Plus />
        {{ addLabel }}
      </Button>
    </CardContent>
  </Card>
</template>
